import {datadogRum} from '@datadog/browser-rum';

import {assert} from 'Asserts';
import {distribution} from 'metrics/Metrics';
import {
  clearProfilerTimingEvent,
  getProfilerEventValue,
  measureTimingBetweenEvents,
  processTimingEvent,
} from 'performance/BenchlingProfiler';

import {toSeconds} from './PerformanceUtils';
import {ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

export enum ProfilerInventoryFeatures {
  LOCATION_PICKER_FETCH = 'locationPickerFetch',
  LOCATION_PICKER_SEARCH = 'locationPickerSearch',
  LOCATION_PICKER_RENDER = 'locationPickerRender',
  GRID_VIEW_RENDER = 'gridViewRender',
  PLATE_ANNOTATE_FILL = 'plateAnnotateFill',
  CREATE_PLATE_DESIGN = 'createPlateDesign',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerInventoryFeatures]: Function} = {
  [ProfilerInventoryFeatures.LOCATION_PICKER_FETCH]: processLocationPickerFetch,
  [ProfilerInventoryFeatures.LOCATION_PICKER_RENDER]: processLocationPickerRender,
  [ProfilerInventoryFeatures.LOCATION_PICKER_SEARCH]: processLocationPickerSearch,
  [ProfilerInventoryFeatures.GRID_VIEW_RENDER]: processGridViewRender,
  [ProfilerInventoryFeatures.PLATE_ANNOTATE_FILL]: processPlateAnnotateFill,
  [ProfilerInventoryFeatures.CREATE_PLATE_DESIGN]: processCreatePlateDesign,
};

enum ProfilerInventoryActions {
  SEARCH_STARTED = 'searchStarted',
  SEARCH_FINISHED = 'searchFinished',
  FETCH_STARTED = 'fetchStarted',
  FETCH_FINISHED = 'fetchFinished',
  RENDER_STARTED = 'renderStarted',
  RENDER_FINISHED = 'renderFinished',
  ANNOTATE_FILL_STARTED = 'annotateFillStarted',
  ANNOTATE_FILL_FINISHED = 'annotateFillFinished',
  CREATE_PLATE_DESIGN_STARTED = 'createPlateDesignStarted',
  CREATE_PLATE_DESIGN_FINISHED = 'createPlateDesignFinished',
}

export const LOCATION_PICKER_SEARCH_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.LOCATION_PICKER_SEARCH,
  action: ProfilerInventoryActions.SEARCH_STARTED,
};
export const LOCATION_PICKER_SEARCH_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.LOCATION_PICKER_SEARCH,
  action: ProfilerInventoryActions.SEARCH_FINISHED,
};
export const LOCATION_PICKER_FETCH_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.LOCATION_PICKER_FETCH,
  action: ProfilerInventoryActions.FETCH_STARTED,
};
export const LOCATION_PICKER_FETCH_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.LOCATION_PICKER_FETCH,
  action: ProfilerInventoryActions.FETCH_FINISHED,
};
export const LOCATION_PICKER_RENDER_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.LOCATION_PICKER_RENDER,
  action: ProfilerInventoryActions.RENDER_STARTED,
};
export const LOCATION_PICKER_RENDER_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.LOCATION_PICKER_RENDER,
  action: ProfilerInventoryActions.RENDER_FINISHED,
};

export const GRID_VIEW_RENDER_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.GRID_VIEW_RENDER,
  action: ProfilerInventoryActions.RENDER_STARTED,
};
export const GRID_VIEW_RENDER_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.GRID_VIEW_RENDER,
  action: ProfilerInventoryActions.RENDER_FINISHED,
};
export const PLATE_ANNOTATE_FILL_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.PLATE_ANNOTATE_FILL,
  action: ProfilerInventoryActions.ANNOTATE_FILL_STARTED,
};
export const PLATE_ANNOTATE_FILL_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.PLATE_ANNOTATE_FILL,
  action: ProfilerInventoryActions.ANNOTATE_FILL_FINISHED,
};
export const CREATE_PLATE_DESIGN_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.CREATE_PLATE_DESIGN,
  action: ProfilerInventoryActions.CREATE_PLATE_DESIGN_STARTED,
};
export const CREATE_PLATE_DESIGN_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.INVENTORY,
  feature: ProfilerInventoryFeatures.CREATE_PLATE_DESIGN,
  action: ProfilerInventoryActions.CREATE_PLATE_DESIGN_FINISHED,
};

export function getGridSizeBucket(numberCells: number): string {
  if (Number.isNaN(numberCells)) {
    return 'unknown';
  }
  if (numberCells > 384) {
    return 'gt_384';
  } else if (numberCells > 96) {
    return '97_to_384';
  } else if (numberCells > 48) {
    return '49_to_96';
  } else if (numberCells > 24) {
    return '25_to_48';
  } else {
    return 'lt_25';
  }
}

export function processInventoryEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerInventoryFeatures;
  assert(
    Object.values(ProfilerInventoryFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

function processLocationPickerRender(): void {
  const renderStartTime = getProfilerEventValue(LOCATION_PICKER_RENDER_STARTED_EVENT);
  const renderFinishTime = getProfilerEventValue(LOCATION_PICKER_RENDER_FINISHED_EVENT);

  if (renderFinishTime != null && renderStartTime != null) {
    const timeToRender = toSeconds(renderFinishTime.timestamp - renderStartTime.timestamp);
    const numItemsRendered = renderFinishTime.extra?.numItemsRendered;

    if (timeToRender < 0 || numItemsRendered !== renderStartTime.extra?.numItemsToRender) {
      // There was a leftover rendered event from a previous load. This clears the state and does not send
      // the metric.
      clearProfilerTimingEvent(LOCATION_PICKER_RENDER_FINISHED_EVENT);
      return;
    }

    const hasSelectedItem = renderFinishTime.extra?.hasSelectedItem;
    datadogRum.addAction('Location picker: Render', {time: timeToRender, numItemsRendered, hasSelectedItem});

    clearProfilerTimingEvent(LOCATION_PICKER_RENDER_STARTED_EVENT);
    clearProfilerTimingEvent(LOCATION_PICKER_RENDER_FINISHED_EVENT);
  }
}

function processLocationPickerFetch(): void {
  const fetchStartedTime = getProfilerEventValue(LOCATION_PICKER_FETCH_STARTED_EVENT);
  const fetchResultsReturnedTime = getProfilerEventValue(LOCATION_PICKER_FETCH_FINISHED_EVENT);

  if (fetchStartedTime != null && fetchResultsReturnedTime != null) {
    const timeToFetch = toSeconds(fetchResultsReturnedTime.timestamp - fetchStartedTime.timestamp);
    const originFunction = fetchResultsReturnedTime.extra?.function;
    if (timeToFetch < 0 || originFunction !== fetchStartedTime.extra?.function) {
      // If the time is negative, there was a leftover rendered event timing from a previous search
      // This clears the state, and doesn't send the metric
      clearProfilerTimingEvent(LOCATION_PICKER_FETCH_FINISHED_EVENT);
      return;
    }

    const fetchLimit = fetchStartedTime.extra?.queryMax;
    const numItemsFetched = fetchResultsReturnedTime.extra?.numItemsFetched;
    datadogRum.addAction('Location picker: Fetch', {
      time: timeToFetch,
      fetchLimit,
      numItemsFetched,
      function: originFunction,
    });

    clearProfilerTimingEvent(LOCATION_PICKER_FETCH_STARTED_EVENT);
    clearProfilerTimingEvent(LOCATION_PICKER_FETCH_FINISHED_EVENT);
  }
}

function processLocationPickerSearch(): void {
  const searchStartedTime = getProfilerEventValue(LOCATION_PICKER_SEARCH_STARTED_EVENT);
  const searchResultsReturnedTime = getProfilerEventValue(LOCATION_PICKER_SEARCH_FINISHED_EVENT);

  if (searchStartedTime != null && searchResultsReturnedTime) {
    const timeToSearch = toSeconds(searchResultsReturnedTime.timestamp - searchStartedTime.timestamp);
    const component = searchResultsReturnedTime.extra?.component;
    if (timeToSearch < 0 || component !== searchStartedTime.extra?.component) {
      // If the time is negative, there was a leftover rendered event timing from a previous search
      // This clears the state, and doesn't send the metric
      clearProfilerTimingEvent(LOCATION_PICKER_SEARCH_FINISHED_EVENT);
      return;
    }

    const searchLimit = searchStartedTime.extra?.searchLimit;
    const numItemsReturned = searchResultsReturnedTime.extra?.numItemsReturned;
    datadogRum.addAction('Location picker: Input search', {
      time: timeToSearch,
      numItemsReturned,
      searchLimit,
      component,
    });

    clearProfilerTimingEvent(LOCATION_PICKER_SEARCH_STARTED_EVENT);
    clearProfilerTimingEvent(LOCATION_PICKER_SEARCH_FINISHED_EVENT);
  }
}

function processGridViewRender(): void {
  const measurement = measureTimingBetweenEvents(
    GRID_VIEW_RENDER_STARTED_EVENT,
    GRID_VIEW_RENDER_FINISHED_EVENT
  );
  if (measurement == null) {
    return;
  }

  const {time, endEvent} = measurement;
  if (time <= 0.2 || time >= 30 * 60) {
    // Disregard timing of more than 30 minutes since this might be an edge case.
    // TODO(BNCH-103214): Grid view rerenders unnecessarily, so only track the timing above 200ms
    return;
  }
  distribution('perf.grid_view_render', time, {
    grid_size_bucket: getGridSizeBucket(Number(endEvent.extra?.numTotalCells)),
  });

  datadogRum.addAction('Grid page: Render', {
    time,
    numTotalCells: endEvent.extra?.numTotalCells,
    numSelectedCells: endEvent.extra?.numSelectedCells,
  });
}

function processPlateAnnotateFill(): void {
  processTimingEvent(
    PLATE_ANNOTATE_FILL_STARTED_EVENT,
    PLATE_ANNOTATE_FILL_FINISHED_EVENT,
    'Annotate and fill plate modal: Save',
    'perf.annotate_fill_plate',
    1
  );
}

function processCreatePlateDesign(): void {
  processTimingEvent(
    CREATE_PLATE_DESIGN_STARTED_EVENT,
    CREATE_PLATE_DESIGN_FINISHED_EVENT,
    'Create plate design: Save',
    'perf.create_plate_design',
    1
  );
}
